import {
  Box,
  Button,
  Flex,
  Typography,
  Image,
  Picture,
} from "@jewlr/storybook/core"
import { useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import styled from "styled-components"

import {
  activateAccountMenu,
  setExplicitLogin,
  setHeaderDropdown,
} from "app-store-v2/app/actions"
import { openModal } from "components/modal"
import { jewlr_t, IS_UK } from "helpers/application"
import useWindowDimensions from "helpers/window-dimensions"

const SectionTitle = styled(Typography).attrs((props) => ({
  as: "h2",
  fontSize: "26px",
  fontWeight: 700,
  lineHeight: "30px",
  textAlign: "center",
  ...props,
}))`
  text-transform: capitalize;
`

const SectionSubTitle = styled(Typography).attrs((props) => ({
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: "20px",
  textAlign: "center",
  ...props,
}))``

const StyledFlex = styled(Flex).attrs({
  alignItems: "center",
  border: 1,
  borderColor: "grey.20",
  borderStyle: "solid",
  flexDirection: "column",
  mb: "50px",
  padding: "28px 24px",
})`
  ${(props) => props.theme.mediaQueries.desktop`
    margin-bottom: 0;
    &:not(:first-child):not(:last-child) {
      margin: 0 24px;
    }
  `}
`

const StyledLink = styled(Typography).attrs({
  as: Link,
  color: "link",
})`
  font-size: 16px;
  font-weight: 300;
  letter-spacing: -0.02em;
  line-height: 20px;
  text-decoration: underline;
  &:hover,
  &:active,
  &:visited {
    color: ${(props) => props.theme.colors.primary};
  }
`

const SignUpButton = styled(Button).attrs((props) => ({
  alignItems: "center",
  borderRadius: 5,
  display: "flex",
  fontWeight: 500,
  height: "44px",
  justifyContent: "center",
  lineHeight: "16px",
  variant: "primary",
  width: "220px",
  ...props,
}))``

const StyledList = styled.ul`
  margin: 0;
  padding-left: 12px;
  ${(props) => props.theme.mediaQueries.desktop`
    padding-left: 20px;
    padding-right: 25px;
  `}
`

const StyledListItem = styled(Typography).attrs({
  as: "li",
  fontSize: 16,
  fontWeight: 500,
  letterSpacing: "-0.02em",
  lineHeight: "28px",
})`
  ${(props) => props.theme.mediaQueries.mobileOnly`
    &:nth-child(3) {
      letter-spacing: -0.04em;
    }
  `}
`

const PictureWrapper = styled.div`
  height: 100%;
  width: 100%;
  picture {
    height: 100%;
    width: 100%;
  }
  img {
    object-fit: cover;
  }
`

const VipRewards = () => {
  const dispatch = useDispatch()
  const loggedIn = useSelector(
    (state) => Object.keys(state.user.jewlrorders).length > 0
  )
  const { isTablet } = useWindowDimensions()

  const handleSignUpClick = useCallback(() => {
    if (loggedIn) {
      if (isTablet) {
        dispatch(activateAccountMenu())
      } else {
        dispatch(setHeaderDropdown("account"))
      }
    } else {
      dispatch(setExplicitLogin())
      openModal("#signup")
    }
  }, [loggedIn, isTablet])

  const dollarSingular = IS_UK() ? "pound" : "dollar"
  const dollarUnit = IS_UK() ? "pounds" : "dollars"

  const stepsData = [
    {
      alt: "step 1",
      imgSrc: require("images/vip-rewards/account.svg"),
      text: <>1. Activate your account.</>,
    },
    {
      alt: "step 2",
      imgSrc: require("images/vip-rewards/earn.svg"),
      text: (
        <>
          2. Earn 10, 12 or 14 <br />
          Jewls per {dollarSingular} spent on <br />
          every new purchase.
        </>
      ),
    },
    {
      alt: "step 3",
      imgSrc: require("images/vip-rewards/new-levels.svg"),
      text: (
        <>
          3. Reach new levels <br />
          to unlock exclusive <br />
          member-only offers.
        </>
      ),
    },
    {
      alt: "step 4",
      imgSrc: require("images/vip-rewards/redeem.svg"),
      text: (
        <>
          4. Redeem Jewls <br />
          for {dollarUnit} on future <br />
          purchases at checkout.
        </>
      ),
    },
  ]

  const programData = [
    {
      alt: "Sapphire",
      details: [
        "0 – 4,999 Jewls",
        "Earn 10 Jewls for every $1 spent",
        "Early access to new products and exclusive offers",
        "1,000 bonus Jewls for sign up",
      ],
      imgSrc: require("images/vip-rewards/sapphire.svg"),
      title: "Sapphire",
    },
    {
      alt: "Ruby",
      details: [
        "5,000 – 8,499 Jewls",
        "Earn 12 Jewls for every $1 spent",
        "Early access to new products and exclusive offers",
        `Complimentary Expedited ${jewlr_t("terms.shipping")}`,
      ],
      imgSrc: require("images/vip-rewards/ruby.svg"),
      title: "Ruby",
    },
    {
      alt: "Diamond",
      details: [
        "8,500+ Jewls",
        "Earn 14 Jewls for every $1 spent",
        "Early access to new products and exclusive offers",
        `Complimentary Expedited ${jewlr_t("terms.shipping")}`,
      ],
      imgSrc: require("images/vip-rewards/diamond.svg"),
      title: "Diamond",
    },
  ]

  return (
    <>
      <Helmet>
        <title>VIP Rewards</title>
      </Helmet>
      <Box containWide position="relative" width="100%">
        <Box height="100%" position="absolute" width="100%">
          <Flex
            contain
            flexDirection="column"
            height="100%"
            justifyContent="center"
            position={{ _: "absolute", tablet: "static" }}
            px={{ _: "30px", tablet: 0 }}
            top={{ _: "25%", tablet: 0 }}
          >
            <Typography
              as="h1"
              fontSize={32}
              fontWeight={700}
              lineHeight="36px"
              mb={{ _: "14px", tablet: 2 }}
              width={{ _: "211px", tablet: "auto" }}
            >
              VIP Rewards: Join for Jewls!
            </Typography>
            <Typography
              fontSize={{ _: 18, tablet: 20 }}
              fontWeight={500}
              lineHeight={{ _: "21px", tablet: "24px" }}
              width={{ _: "316px", tablet: "400px" }}
            >
              Members earn Jewls on every new order, redeemable for dollars on
              future purchases.
            </Typography>
            <Typography
              fontSize={{ _: 18, tablet: 20 }}
              fontWeight={700}
              lineHeight={{ _: "21px", tablet: "24px" }}
              mb={{ _: "14px", tablet: 3 }}
            >
              Join today and receive 1,000 bonus Jewls.
            </Typography>
            <SignUpButton onClick={handleSignUpClick}>
              Sign Up Now!
            </SignUpButton>
          </Flex>
        </Box>
        <Picture
          alt="Banner"
          fallback={require("images/vip-rewards/top-banner/desktop.jpg")}
          srcSet={[
            {
              height: "2020",
              media: "(max-width: 767px)",
              sizes: "100%",
              srcSet: `${require("images/vip-rewards/top-banner/mobile_2x.jpg")} 1534w`,
              width: "1534",
            },
            {
              height: "367",
              media: "(min-width: 768px) and (max-width: 1023px)",
              sizes: "100%",
              srcSet: `
                ${require("images/vip-rewards/top-banner/tablet.jpg")} 1024w,
                ${require("images/vip-rewards/top-banner/tablet_2x.jpg")} 2048w
              `,
              width: "1024",
            },
            {
              height: "469",
              media: "(min-width: 1024px)",
              sizes: "1920px",
              srcSet: `
                ${require("images/vip-rewards/top-banner/desktop.jpg")} 1440w,
                ${require("images/vip-rewards/top-banner/desktop_2x.jpg")} 2880w
              `,
              width: "1440",
            },
          ]}
        />
      </Box>
      <Box>
        <Box contain pb={{ _: 9, desktop: 8 }} pt={{ _: 7, desktop: 8 }}>
          <SectionTitle as="h2" mb="12px">
            How It Works
          </SectionTitle>
          <SectionSubTitle mb={{ _: 0, desktop: "30px" }}>
            Easy-to-earn points on every new purchase.
          </SectionSubTitle>
          <Flex
            alignItems={{ _: "center", desktop: "start" }}
            flexDirection={{ _: "column", desktop: "row" }}
            justifyContent="space-between"
          >
            {stepsData.map((el, i) => (
              <Flex
                alignItems="center"
                flexDirection="column"
                key={el.alt}
                width="160px"
              >
                <Image
                  alt={el.alt + i}
                  mb={{ _: 2, desktop: "26px" }}
                  mt={{ _: "32px", desktop: "10px" }}
                  ratio="1 / 1"
                  src={el.imgSrc}
                />
                <Typography
                  fontSize={18}
                  fontWeight={500}
                  lineHeight="20px"
                  textAlign="center"
                  width="180px"
                >
                  {el.text}
                </Typography>
              </Flex>
            ))}
          </Flex>
        </Box>
        <Box containWide>
          <Flex
            flexDirection={{ _: "column", desktop: "row" }}
            height={{ _: "auto", desktop: "390px" }}
          >
            <Flex
              flex={1}
              flexBasis={{ _: "260px", desktop: "min-content" }}
              order={{ _: 1, desktop: 0 }}
            >
              <PictureWrapper>
                <Picture
                  alt="Banner"
                  fallback={require("images/vip-rewards/features-modelling/desktop.jpg")}
                  height="100%"
                  srcSet={[
                    {
                      height: "260",
                      media: "(max-width: 767px)",
                      sizes: "100%",
                      srcSet: `
                        ${require("images/vip-rewards/features-modelling/mobile.jpg")} 375w,
                        ${require("images/vip-rewards/features-modelling/mobile_2x.jpg")} 750w
                      `,
                      width: "375",
                    },
                    {
                      height: "391",
                      media: "(min-width: 768px)",
                      sizes: "100%",
                      srcSet: `
                        ${require("images/vip-rewards/features-modelling/desktop.jpg")} 720w,
                        ${require("images/vip-rewards/features-modelling/desktop_2x.jpg")} 1440w
                      `,
                      width: "720",
                    },
                  ]}
                />
              </PictureWrapper>
            </Flex>
            <Flex
              alignItems="center"
              bg={{ _: "#F1F3F8", desktop: "white" }}
              flex={1}
              flexBasis={{ _: "162px", desktop: "min-content" }}
              flexDirection="column"
              justifyContent="center"
              order={{ _: 0, desktop: 1 }}
            >
              <Typography
                fontSize={{ _: 20, desktop: 22 }}
                fontWeight={700}
                lineHeight={{ _: "22px", desktop: "30px" }}
                mb={2}
                px={{ _: 10, desktop: 0 }}
                textAlign="center"
              >
                Redeem Jewls For Dollars On The Jewelry You Love:
              </Typography>
              <Typography
                fontSize={{ _: 40, desktop: 52 }}
                fontWeight={700}
                letterSpacing="-0.02em"
                lineHeight={{ _: "48px", desktop: "62px" }}
              >
                200 Jewls = $1
              </Typography>
            </Flex>
          </Flex>
        </Box>
        <Box contain pb={{ _: 0, desktop: 8 }} pt={{ _: 9, desktop: 8 }} px={2}>
          <SectionTitle as="h2" mb="12px">
            VIP Rewards Program
          </SectionTitle>
          <SectionSubTitle mb={{ _: 3, desktop: "30px" }}>
            Earn more perks the more you shop.
          </SectionSubTitle>
          <Flex
            alignItems={{ _: "center", desktop: "start" }}
            flexDirection={{ _: "column", desktop: "row" }}
            justifyContent="space-between"
          >
            {programData.map((p, i) => (
              <StyledFlex key={p.title}>
                <Typography
                  fontSize={24}
                  fontWeight={500}
                  lineHeight="24px"
                  textAlign="center"
                >
                  {p.title}
                </Typography>
                <Flex justifyContent="center">
                  <Image
                    alt={p.alt + i}
                    mb="20px"
                    mt="20px"
                    mx="auto"
                    ratio="199 / 150"
                    src={p.imgSrc}
                    width="116px"
                  />
                </Flex>
                <StyledList>
                  {p.details.map((d, i) => (
                    <StyledListItem key={i}>{d}</StyledListItem>
                  ))}
                </StyledList>
              </StyledFlex>
            ))}
          </Flex>
        </Box>
      </Box>
      <Flex
        alignItems="center"
        bg="#F1F3F8"
        flexDirection="column"
        maxWidth="none"
        mb={{ tablet: "-32px" }}
        pb="50px"
        pt={7}
      >
        <Typography
          fontSize={26}
          fontWeight={700}
          lineHeight="30px"
          textAlign="center"
        >
          Join today for FREE and <br />
          receive 1,000 bonus Jewls.
        </Typography>
        <SignUpButton mb={3} mt="10px" onClick={handleSignUpClick}>
          Sign Up Today!
        </SignUpButton>
        <StyledLink to="/helpdesk/5e9f51622c7d3a7e9aeb5bfc/5ea1a14c2c7d3a7e9aeb8024">
          Click here for frequently asked questions.
        </StyledLink>
      </Flex>
    </>
  )
}

export default VipRewards
